import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ClipStatusType } from '@/types/status';
import { CLIP_STATUS_TYPES, getStatusesForType } from '@/types/status';

interface StatusSelectorProps {
  value: string | null;
  statusType: ClipStatusType | null;
  onChange: (status: string, statusType: ClipStatusType) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Two-dropdown selector for clip status
 * First dropdown: status type (vfx_comp, stock_footage)
 * Second dropdown: specific status based on type
 */
export function StatusSelector({
  value,
  statusType,
  onChange,
  disabled,
  className
}: StatusSelectorProps) {
  const [selectedType, setSelectedType] = useState<ClipStatusType | null>(statusType);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(value);

  // Sync with external changes
  useEffect(() => {
    setSelectedType(statusType);
    setSelectedStatus(value);
  }, [statusType, value]);

  const availableStatuses = selectedType ? getStatusesForType(selectedType) : [];

  const handleTypeChange = (type: string) => {
    const newType = type as ClipStatusType;
    setSelectedType(newType);
    // Reset status when type changes
    setSelectedStatus(null);
  };

  const handleStatusChange = (status: string) => {
    if (!selectedType) return;
    setSelectedStatus(status);
    onChange(status, selectedType);
  };

  return (
    <div className={`flex gap-2 ${className || ''}`}>
      {/* Status Type Dropdown */}
      <Select
        value={selectedType || ''}
        onValueChange={handleTypeChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="Select type" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(CLIP_STATUS_TYPES).map(([key, config]) => (
            <SelectItem key={key} value={key}>
              {config.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Status Dropdown - only show when type is selected */}
      {selectedType && (
        <Select
          value={selectedStatus || ''}
          onValueChange={handleStatusChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            {availableStatuses.map(status => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
